import { createSlice } from '@reduxjs/toolkit';
import { canDisarmShimmer, readShimmerPayload } from '@shared/lib/tableShimmer';
import { REQUEST_STATUS } from '../constants';
import { fetchTableSettings } from '../api';

const defaultViewedState = {
  newViewed: [],
  notViewedUuids: [],
  wasSentViewedUuids: [],
};

export const tableSlice = createSlice({
  name: 'table',
  initialState: {
    settings: null,
    status: REQUEST_STATUS.idle,
    error: null,
    canShowShimmer: false,
    // Which table armed the shimmer. `null` means "every table", which
    // is what the bulk-action features dispatch — they know their list
    // is about to reload and do not pass a key. A table that arms the
    // flag for its own sort / filter change passes its own key, so a
    // second table mounted at the same time (a patient picker inside a
    // panel over a list page) does not paint skeletons for someone
    // else's reload.
    shimmerTableKey: null,
    viewState: defaultViewedState,
  },
  reducers: {
    updateColumnOrderByTableName: (state, action) => {
      // Guard against missing preferences buckets — a newly-introduced
      // table has no entry until the backend has added the key to its
      // preferences schema. Seed an empty bucket on demand so client
      // state is authoritative and doesn't crash on undefined access.
      if (!state.settings) state.settings = {};
      if (!state.settings[action.payload.tableKey]) state.settings[action.payload.tableKey] = {};
      state.settings[action.payload.tableKey].columnOrder = [...action.payload.newOrder];
    },
    updateColumnVisibilityByTableName: (state, action) => {
      if (!state.settings) state.settings = {};
      if (!state.settings[action.payload.tableKey]) state.settings[action.payload.tableKey] = {};
      state.settings[action.payload.tableKey].columnVisibility = action.payload.newVisibility;
    },
    updateColumnSizingByTableName: (state, action) => {
      if (!state.settings) state.settings = {};
      if (!state.settings[action.payload.tableKey]) state.settings[action.payload.tableKey] = {};
      state.settings[action.payload.tableKey].columnSizes = action.payload.newColumnSizing;
    },
    updateColumnPinningByTableName: (state, action) => {
      if (!state.settings) state.settings = {};
      if (!state.settings[action.payload.tableKey]) state.settings[action.payload.tableKey] = {};
      state.settings[action.payload.tableKey].columnPinning = action.payload.newColumnPinning;
    },
    updateShowColumnFilters: (state, action) => {
      if (!state.settings) state.settings = {};
      if (!state.settings.commonTable) state.settings.commonTable = {};
      state.settings.commonTable.showColumnFilters = action.payload;
    },
    updateDensity: (state, action) => {
      if (!state.settings) state.settings = {};
      if (!state.settings.commonTable) state.settings.commonTable = {};
      state.settings.commonTable.density = action.payload;
    },
    updateIsFullScreen: (state, action) => {
      state.settings.isFullScreen = action.payload.newIsFullScreen;
    },
    /**
     * `updateCanShowShimmer(true)` — every table shimmers, as before.
     * `updateCanShowShimmer({ value: true, tableKey })` — only that one.
     *
     * Disarming is scoped the same way: a table may only clear a flag
     * that is unowned or its own, so the first table to finish loading
     * no longer cuts short the shimmer of a table still fetching.
     */
    updateCanShowShimmer: (state, action) => {
      const { value, tableKey } = readShimmerPayload(action.payload);

      if (value) {
        state.canShowShimmer = true;
        state.shimmerTableKey = tableKey;
        return;
      }

      if (canDisarmShimmer(state.shimmerTableKey, tableKey)) {
        state.canShowShimmer = false;
        state.shimmerTableKey = null;
      }
    },
    addViewedState: (state, action) => {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },
    // A row scrolled into view. `wasSentViewedUuids` is misnamed — it is
    // written here, on viewing, not on sending — and it drives the row's
    // immediate "viewed" tint. `newViewed` is the batch still owed to the
    // server. The spread keeps `notViewedUuids`: without it the first
    // viewed row wiped the list every other row's tint is read from.
    addViewedItemState: (state, action) => {
      state.viewState = {
        ...state.viewState,
        wasSentViewedUuids: [...new Set([...state.viewState.wasSentViewedUuids, action.payload])],
        newViewed: [...new Set([...state.viewState.newViewed, action.payload])],
      };
    },
    // The batch was handed to the server: take it out of what is owed.
    // Called BEFORE the request is dispatched, so an effect that watches
    // `newViewed` sees an empty batch and cannot send the same uuids
    // again — which is what the Alerts page did, once a second, forever.
    markViewedSent: (state, action) => {
      const sent = new Set(action.payload || []);
      state.viewState = {
        ...state.viewState,
        newViewed: (state.viewState.newViewed || []).filter(uuid => !sent.has(uuid)),
      };
    },
    resetViewedRows: state => {
      state.viewState = defaultViewedState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTableSettings.pending, state => {
        state.status = REQUEST_STATUS.loading;
      })
      .addCase(fetchTableSettings.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error =
          action.payload?.error || action.error?.message || 'Failed to load table settings';
      })
      .addCase(fetchTableSettings.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
          const preferences = action.payload.data.data.preferences;

          state.settings = preferences;
          state.error = null;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action?.payload?.error?.message;
        }
      });
  },
});

export const {
  updateColumnOrderByTableName,
  updateColumnVisibilityByTableName,
  updateColumnSizingByTableName,
  updateColumnPinningByTableName,
  updateShowColumnFilters,
  updateDensity,
  updateIsFullScreen,
  updateCanShowShimmer,
  addViewedState,
  resetViewedRows,
  addViewedItemState,
  markViewedSent,
} = tableSlice.actions;

export default tableSlice.reducer;
